import type { ReactElement, RefObject } from 'react'
import { AlertCircle, Eye, EyeOff, Loader2, Shield, X } from 'lucide-react'
import type { TFunction } from 'i18next'

const FONT = { fontFamily: 'Inter, sans-serif' } as const

export function CloseButton({
  onClose,
  disabled,
  label,
}: {
  onClose: () => void
  disabled: boolean
  label: string
}): ReactElement {
  return (
    <button
      onClick={onClose}
      disabled={disabled}
      className="absolute top-4 right-4 rounded-lg p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      aria-label={label}
    >
      <X className="h-5 w-5" />
    </button>
  )
}

export function ModalHeader({ t }: { t: TFunction<'auth'> }): ReactElement {
  return (
    <div className="mb-6 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-white/20">
        <Shield className="h-7 w-7 text-white" />
      </div>
      <h2 id="signin-title" className="text-2xl font-semibold text-white" style={FONT}>
        {t('signInModal.title')}
      </h2>
      <p className="mt-2 text-white/70" style={FONT}>
        {t('signInModal.subtitle')}
      </p>
    </div>
  )
}

export function ErrorAlert({ message }: { message: string }): ReactElement {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-400/30 bg-red-500/20 p-3 text-red-100">
      <AlertCircle className="h-5 w-5 shrink-0" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

interface EmailFieldProps {
  emailRef: RefObject<HTMLInputElement>
  value: string
  onChange: (value: string) => void
  disabled: boolean
  t: TFunction<'auth'>
}

export function EmailField({
  emailRef,
  value,
  onChange,
  disabled,
  t,
}: EmailFieldProps): ReactElement {
  return (
    <div>
      <label
        htmlFor="modal-email"
        className="mb-2 block text-sm font-medium text-white/90"
        style={FONT}
      >
        {t('login.emailLabel')}
      </label>
      <input
        ref={emailRef}
        type="email"
        id="modal-email"
        name="email"
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        placeholder={t('login.emailPlaceholder')}
        autoComplete="email"
        required
        disabled={disabled}
        className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-white/40 transition-colors focus:border-white/40 focus:bg-white/15 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        style={FONT}
      />
    </div>
  )
}

interface PasswordFieldProps {
  value: string
  onChange: (value: string) => void
  showPassword: boolean
  onToggleShow: () => void
  disabled: boolean
  t: TFunction<'auth'>
}

export function PasswordField({
  value,
  onChange,
  showPassword,
  onToggleShow,
  disabled,
  t,
}: PasswordFieldProps): ReactElement {
  return (
    <div>
      <label
        htmlFor="modal-password"
        className="mb-2 block text-sm font-medium text-white/90"
        style={FONT}
      >
        {t('login.passwordLabel')}
      </label>
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          id="modal-password"
          name="password"
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          placeholder={t('login.passwordPlaceholder')}
          autoComplete="current-password"
          required
          disabled={disabled}
          className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 pr-12 text-white placeholder-white/40 transition-colors focus:border-white/40 focus:bg-white/15 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          style={FONT}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute top-1/2 right-3 -translate-y-1/2 text-white/50 hover:text-white/80"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  )
}

export function SubmitButton({
  isLoading,
  t,
}: {
  isLoading: boolean
  t: TFunction<'auth'>
}): ReactElement {
  return (
    <button
      type="submit"
      disabled={isLoading}
      className="flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 font-semibold text-violet-600 transition-all hover:bg-white/90 focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      style={FONT}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          {t('login.submittingButton')}
        </>
      ) : (
        t('login.submitButton')
      )}
    </button>
  )
}
